import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { discardOfflineSale, getFailedOfflineSales, type OfflineSale } from '@/lib/offline-db';
import { refreshPendingCount, runOfflineSync } from '@/lib/offline-sync-engine';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useOfflineSyncPanel } from '@/stores/offline-sync-panel.store';

interface SnapshotItem {
  product?: { name?: string };
  variant?: { name?: string } | null;
  quantity?: number;
}
interface Snapshot {
  items?: SnapshotItem[];
  netTotal?: number;
  grandTotal?: number;
}

function describeItems(snap: Snapshot): string {
  const items = snap.items ?? [];
  if (items.length === 0) return '—';
  return items
    .map((i) => `${i.product?.name ?? 'Item'}${i.variant?.name ? ` (${i.variant.name})` : ''} × ${i.quantity ?? 1}`)
    .join(', ');
}

/**
 * The sync panel promised by the "check sync panel" toast (POS-I65): every offline sale the server
 * refused, with its receipt number, when it was rung up, what was sold, the total, and the server's
 * reason, and a way out for each: Retry (after the cause is fixed, e.g. stock received) or Discard
 * (the sale is taken out of the queue; the customer's receipt is then handled by the shop).
 */
export function OfflineSyncPanel() {
  const { open, hide } = useOfflineSyncPanel();
  const { failedCount } = useOfflineSync();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<OfflineSale[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setRows(await getFailedOfflineSales());
  }, []);

  useEffect(() => {
    if (open) void reload();
  }, [open, failedCount, reload]);

  const retry = async (id: string) => {
    setBusyId(id);
    try {
      await runOfflineSync({ includeFailed: true, onlyIds: [id], queryClient });
    } finally {
      setBusyId(null);
      await reload();
    }
  };

  const discard = async (id: string) => {
    setBusyId(id);
    try {
      await discardOfflineSale(id);
      setConfirmId(null);
      await refreshPendingCount();
    } finally {
      setBusyId(null);
      await reload();
    }
  };

  return (
    <Dialog
      visible={open}
      onHide={hide}
      header="Offline sales that did not sync"
      style={{ width: '40rem', maxWidth: '95vw' }}
      draggable={false}
      resizable={false}
      modal
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="offline-sync-empty">
          Nothing is waiting. Every offline sale has been sent to the server.
        </p>
      ) : (
        <div className="space-y-3" data-testid="offline-sync-list">
          <p className="text-xs text-muted-foreground">
            The customer has already paid for these sales. The server refused them for the reason shown. Fix the
            cause (for example receive the missing stock) and press Retry, or Discard the sale if it was entered by mistake.
          </p>
          {rows.map((row) => {
            const snap = (row.cartSnapshot ?? {}) as Snapshot;
            const total = snap.netTotal ?? snap.grandTotal;
            return (
              <div key={row.id} className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm" data-testid="offline-sync-row">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">{row.localInvoiceNumber}</span>
                  <span className="text-xs text-muted-foreground">{new Date(row.createdAt).toLocaleString()}</span>
                </div>
                <p className="mt-1 text-xs">{describeItems(snap)}</p>
                {total != null && <p className="mt-1 text-xs font-medium">Total {formatCurrency(total)}</p>}
                <p className="mt-2 flex items-start gap-1.5 text-xs text-destructive-text">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{row.syncError || 'The server refused this sale.'}</span>
                </p>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {confirmId === row.id ? (
                    <>
                      <span className="me-auto self-center text-xs">Remove this sale from the queue for good?</span>
                      <Button size="sm" variant="outline" onClick={() => setConfirmId(null)} disabled={busyId === row.id}>
                        Keep
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => void discard(row.id)} disabled={busyId === row.id}>
                        Yes, discard
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setConfirmId(row.id)} disabled={busyId === row.id}>
                        <Trash2 className="me-1 h-3.5 w-3.5" />
                        Discard
                      </Button>
                      <Button size="sm" onClick={() => void retry(row.id)} disabled={busyId === row.id}>
                        <RefreshCw className={`me-1 h-3.5 w-3.5 ${busyId === row.id ? 'animate-spin' : ''}`} />
                        Retry
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
